import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Button, CircularProgress, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar, Alert, Chip, Switch, FormControlLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Idea } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_EMAILS } from '../constants';
import { formatDate } from '../utils/formatDate';

const AdminPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [allIdeas, setAllIdeas] = useState<Idea[]>([]);
  const [filterApproved, setFilterApproved] = useState(true);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmailMap, setUserEmailMap] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Check if user is admin
  const isAdmin = currentUser?.email && ADMIN_EMAILS.includes(currentUser.email);

  useEffect(() => {
    // Redirect if not admin
    if (!isAdmin && !loading) {
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  // Fetch all ideas
  useEffect(() => {
    const fetchIdeas = async () => {
      if (!isAdmin) return;
      
      try {
        setLoading(true);
        // Fetch all ideas
        const fetchedIdeas = await api.getIdeas('mostRecent');
        setAllIdeas(fetchedIdeas);
      } catch (err) {
        console.error('Failed to fetch ideas:', err);
        setError('Failed to load ideas. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchIdeas();
  }, [isAdmin]);

  // Fetch all users to get emails
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAdmin) return;
      
      try {
        setUsersLoading(true);
        // Fetch all users
        const users = await api.getUsers();
        
        // Create a mapping of user IDs to emails
        const emailMap: Record<string, string> = {};
        users.forEach(user => {
          if (user.database_uuid && user.email) {
            emailMap[user.database_uuid] = user.email;
          }
        });
        
        setUserEmailMap(emailMap);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [isAdmin]);

  const handleApprove = async (ideaId: string) => {
    try {
      await api.approveIdea(ideaId, true);
      // Update the idea in the list
      setAllIdeas(prev => 
        prev.map(idea => 
          idea.id === ideaId ? { ...idea, approved: true } : idea
        )
      );
      setSnackbar({
        open: true,
        message: 'Idea approved successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to approve idea:', err);
      setSnackbar({
        open: true,
        message: 'Failed to approve idea',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterApproved(event.target.checked);
  };

  // Get email for a user by their ID
  const getUserEmail = (userId: string | undefined): string => {
    if (!userId) return 'N/A';
    return userEmailMap[userId] || 'Email not found';
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Container>
    );
  }

  if (loading || usersLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading data...</Typography>
      </Container>
    );
  }

  // Filter ideas based on the toggle state
  const displayedIdeas = filterApproved 
    ? allIdeas.filter(idea => !idea.approved) 
    : allIdeas;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Admin Panel - Idea Approval
      </Typography>
      
      <FormControlLabel
        control={<Switch checked={filterApproved} onChange={handleFilterChange} />}
        label="Show Only Unapproved Ideas"
        sx={{ mb: 2 }}
      />
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {displayedIdeas.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>{filterApproved ? 'No ideas waiting for approval' : 'No ideas found'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedIdeas.map((idea) => (
                <TableRow key={idea.id}>
                  <TableCell>
                    <Button 
                      color="primary"
                      // Use template literal for correct navigation
                      onClick={() => navigate(`/idea/${idea.id}`)}
                    >
                      {idea.title}
                    </Button>
                  </TableCell>
                  {/* Use user_display_name for author */}
                  <TableCell>{idea.user_display_name || 'N/A'}</TableCell>
                  <TableCell>{getUserEmail(idea.user_id)}</TableCell>
                  <TableCell>{formatDate(idea.created_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {idea.tags?.map((tag) => (
                        <Chip key={tag.id} label={tag.name} size="small" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={idea.approved ? 'Approved' : 'Pending'} 
                      color={idea.approved ? 'success' : 'warning'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleApprove(idea.id)}
                      // Disable button if idea is already approved
                      disabled={idea.approved}
                      sx={{ mr: 1 }}
                    >
                      Approve
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPage; 